import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import { AutocompleteChangeReason } from "@mui/material/Autocomplete";

interface CustomAutocompleteProps {
  id?: string;
  label?: string;
  placeholder?: string;
  options: { label: string; value: string }[];
  value?: string[];
  onChange: (value: string[]) => void;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
}

const FormMultiSelect: React.FC<CustomAutocompleteProps> = ({
  id,
  placeholder,
  options,
  value,
  onChange,
  setInputValue,
}) => {
  const handleChange = (
    _: React.SyntheticEvent,
    newValue: any,
    reason: string
  ) => {
    if (
      reason === "selectOption" ||
      reason === "createOption" ||
      reason === "removeOption" ||
      reason === "clear"
    ) {
      onChange(
        newValue.map((item: any) =>
          item.value ? item.value : item.toUpperCase()
        )
      );
    }
  };

  // when the user inputs an arbitrary value, it should be added to options
  const enhancedOptions = React.useMemo(() => {
    const valueSet = new Set(value);
    const existingOptions = new Set(options.map((option) => option.value));
    const newOptions = [...options];
    valueSet.forEach((val) => {
      if (!existingOptions.has(val)) {
        newOptions.push({ label: val, value: val });
      }
    });
    return newOptions;
  }, [options, value]);

  return (
    <Autocomplete
      multiple
      id={id}
      options={enhancedOptions}
      freeSolo
      getOptionLabel={(option: any) => option.label}
      value={enhancedOptions.filter((option) => value?.includes(option.value))}
      onChange={(
        event: React.SyntheticEvent,
        value: any[],
        reason: AutocompleteChangeReason
      ) => {
        setInputValue("");
        handleChange(event, value, reason) as any;
      }}
      sx={{ width: "80%" }}
      renderTags={(value: any, getTagProps) =>
        value.map((option: any, index: number) => (
          <Chip
            sx={{
              "& .MuiChip-deleteIcon": {
                color: "white",
              },
            }}
            style={{ color: "white" }}
            variant="outlined"
            label={option.label}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          placeholder={placeholder || "Select items"}
          sx={{
            backgroundColor: "#0c1835",
            "& .MuiFilledInput-root": {
              paddingTop: "0.5rem",
              paddingBottom: "0.5rem",
              paddingLeft: "8px",
            },
            "& input": {
              color: "#b6bdce",
              "::placeholder": {
                color: "#b6bdce",
                opacity: 1,
                fontFamily: '"Lato", sans-serif',
                fontSize: "14px",
                fontWeight: 400,
              },
            },
            "& .MuiAutocomplete-clearIndicator": {
              color: "#b6bdce",
              "&:hover": {
                color: "#ffffff",
              },
            },
          }}
          onChange={(e) => setInputValue(e.target.value)}
        />
      )}
    />
  );
};

export default FormMultiSelect;
